// Shared text field for the sign-in screen (v1.3 ticket 09, RCMC-13). v1.2 rendered a bare <input {...props}>:
// its placeholder was its only name and vanished as soon as the Investor typed, and its error was loose text.
//
//   <Input label="Phone Number" name="phone_no" type="tel" autoComplete="tel-national" inputMode="numeric"
//          maxLength={10} required error={errors.phone_no} />
//
// - `label` is visible, stays put, and is tied to the field by id: it is the field's accessible name (and what
//   speech input matches). A placeholder is optional and only ever supplementary.
// - `hint` (above the field) and `error` (below it) are linked by aria-describedby; an error also sets
//   aria-invalid, so a screen reader says "invalid" and reads the error with the field.
// - `required` is aria-required, not the native attribute (as in Select, ticket 06): a native `required` puts an
//   empty field in the invalid state before the Investor has pressed anything. There is no visible "*": every
//   field on the sign-in screen is required.
// Every other prop (name, type, autoComplete, inputMode, maxLength, disabled, …) goes to the <input>.
import { useId } from "react";
import styles from "./Input.module.css";

/**
 * @param {object} props
 * @param {string} props.label      visible, persistent label; the accessible name
 * @param {string} [props.id]       the input's id (default: a generated one); hint/error ids derive from it
 * @param {string} [props.hint]     instructions, linked by aria-describedby
 * @param {string} [props.error]    error text: linked by aria-describedby and marks the field invalid
 * @param {boolean} [props.required] aria-required
 */
const Input = ({ label, id, hint, error, required = false, ...props }) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined; // no empty attribute

  return (
    <div className={styles["input-container"]}>
      <label htmlFor={inputId} className={styles.label}>{label}</label>
      {hint && <div id={hintId} className={styles.hint}>{hint}</div>}
      <input
        {...props}
        id={inputId}
        aria-required={required ? "true" : undefined}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy}
      />
      {error && <div id={errorId} className={styles["error-message"]}>{error}</div>}
    </div>
  );
};

export default Input;
